<template>
  <component
    :is="renderedComponent"
    class="w-btn"
    :to="to"
    :target="target"
    :aria-hidden="ariaHidden"
    :aria-label="ariaLabel"
    :type="type"
    :disabled="isDisabled"
    :class="componentClasses"
    v-bind="hrefIfComponentIsLink"
  >
    <div class="state-overlay" />

    <div class="w-btn__content">
      <w-loading v-bind="loadingProps">
        <slot name="loading" />
      </w-loading>

      <slot name="before-content" />

      <w-icon
        v-if="iconProps.icon && iconPosition === 'left'"
        v-bind="{ ...iconProps, size: iconProps.size || iconSize }"
        class="w-btn__icon w-btn__icon--left"
      />

      <slot>
        <template v-if="text">
          <span v-if="htmlText" class="w-btn__text" :style="textClipStyles" v-html="text" />

          <span v-else class="w-btn__text" :style="textClipStyles"> {{ translate(text) }} </span>
        </template>
      </slot>

      <w-icon
        v-if="iconProps.icon && iconPosition === 'right'"
        v-bind="{ ...iconProps, size: iconProps.size || iconSize }"
        class="w-btn__icon w-btn__icon--right"
      />

      <slot name="after-content" />
    </div>
  </component>
</template>

<script lang="ts">
// @ts-ignore
import { computed, defineComponent, useSlots } from 'vue';

import { withButtonProps, useButtonProps } from '../../composables/props/button';
import { useTextClipProps } from '../../composables/props/textClip';
import { useTranslation } from '../../composables/translation';
import { ButtonElementType } from '../../enums/buttonElementType.enum';
import WIcon from '../w-icon/WIcon.vue';
import WLoading from '../w-loading/WLoading.vue';

// @vuese
// Przycisk uniwersalny
export default defineComponent({
  components: { WIcon, WLoading },

  props: {
    ...withButtonProps
  },

  setup(props) {
    const componentName = 'w-btn';

    const slots = useSlots();

    /** Tag komponentu*/
    const renderedComponent = computed(() => {
      if (props.to) {
        return ButtonElementType.ROUTER_LINK;
      }
      if (props.href) {
        return ButtonElementType.LINK;
      }
      return ButtonElementType.BUTTON;
    });

    /** Czy przycisk jest zablokowany */
    const isDisabled = computed(() =>
      renderedComponent.value == ButtonElementType.BUTTON ? props.disabled || props.loading : undefined
    );

    /** Aria-hiden przycisku*/
    const ariaHidden = computed(() => (props.label || props.text || slots.default ? undefined : true));

    /** Aria-label przycisku */
    const ariaLabel = computed(() => (props.text ? undefined : translate(props.label)));

    /**
     * Zwraca obiekt do zbindowania z propsem href gdy generowany jest link
     *
     * Rozwiązuje problem z pustymi `a` generowanymi przez `router-link`
     */
    const hrefIfComponentIsLink = computed(() => {
      if (renderedComponent.value === ButtonElementType.LINK) {
        return { href: props.href };
      }
      return {};
    });

    const { translate } = useTranslation();

    return {
      ...useButtonProps(componentName, props),
      ariaHidden,
      ariaLabel,
      hrefIfComponentIsLink,
      renderedComponent,
      isDisabled,
      textClipStyles: useTextClipProps(props).value,

      translate
    };
  }
});
</script>
<style lang="scss">
@use 'WButton';
</style>
